import proj4 from 'proj4';
import {
  geoConicConformal,
  geoMercator,
  geoStereographic,
} from 'd3-geo';
import { geoRobinson } from 'd3-geo-projection';

proj4.defs(
  'EPSG:3006',
  '+proj=utm +zone=33 +ellps=GRS80 +units=m +no_defs +type=crs'
);
proj4.defs(
  'EPSG:32633',
  '+proj=utm +zone=33 +datum=WGS84 +units=m +no_defs +type=crs'
);

const degreeProjection = () => [0, 0];

const createD3Projector = (factory) => {
  const projection = factory().scale(1).translate([0, 0]).precision(0.1);
  return (lng, lat) => {
    const result = projection([lng, lat]);
    if (!result || Number.isNaN(result[0]) || Number.isNaN(result[1])) {
      return null;
    }
    return result;
  };
};

const createProj4Projector = (toCrs) => (lng, lat) => {
  const result = proj4('EPSG:4326', toCrs, [lng, lat]);
  if (!result || Number.isNaN(result[0]) || Number.isNaN(result[1])) {
    return null;
  }
  return result;
};

export const projectionCatalog = {
  epsg4326: {
    id: 'epsg4326',
    name: 'WGS84 Geographic',
    epsg: 'EPSG:4326',
    type: 'Geographic CRS',
    datum: 'WGS84',
    ellipsoid: 'WGS84',
    usage: 'Global GPS and geodetic coordinates',
    distortion: 'No planar projection; angular coordinates on ellipsoid.',
    advantages: 'Global standard and interoperable across GIS stacks.',
    disadvantages: 'Not suitable for planar distance/area calculations.',
    educational: 'WGS84 stores latitude/longitude directly, which is why it is the global baseline.',
    projector: (lng, lat) => [lng, lat],
    bestFor: 'Global positioning and interchange',
  },
  epsg3857: {
    id: 'epsg3857',
    name: 'Web Mercator',
    epsg: 'EPSG:3857',
    type: 'Cylindrical (Pseudo-Mercator)',
    datum: 'WGS84 ensemble',
    ellipsoid: 'Spherical development',
    usage: 'Web mapping tiles and slippy maps',
    distortion: 'Area and scale inflate rapidly toward poles.',
    advantages: 'Conformal and easy for tiled web maps.',
    disadvantages: 'Extreme polar area distortion (Greenland inflation effect).',
    educational: 'Mercator keeps local angles but heavily enlarges high latitudes.',
    projector: createProj4Projector('EPSG:3857'),
    bestFor: 'Navigation angles and web tile systems',
  },
  epsg3006: {
    id: 'epsg3006',
    name: 'SWEREF 99 TM',
    epsg: 'EPSG:3006',
    type: 'Transverse Mercator',
    datum: 'SWEREF99',
    ellipsoid: 'GRS80',
    usage: 'National mapping in Sweden',
    distortion: 'Low distortion in Sweden, grows outside target zone.',
    advantages: 'Highly accurate for Swedish surveying and GIS.',
    disadvantages: 'Not suitable for global mapping.',
    educational: 'A local CRS can outperform global projections in its design region.',
    projector: createProj4Projector('EPSG:3006'),
    bestFor: 'Sweden',
  },
  utm33n: {
    id: 'utm33n',
    name: 'UTM Zone 33N',
    epsg: 'EPSG:32633',
    type: 'Transverse Mercator (UTM)',
    datum: 'WGS84',
    ellipsoid: 'WGS84',
    usage: 'Regional mapping in central Europe and East Africa zone band',
    distortion: 'Low in-zone distortion, increases near zone edges.',
    advantages: 'Reliable metric distances within zone 33N.',
    disadvantages: 'Requires zone management for cross-zone analysis.',
    educational: 'UTM balances distortion by slicing Earth into narrow zones.',
    projector: createProj4Projector('EPSG:32633'),
    bestFor: 'Regional metric workflows',
  },
  robinson: {
    id: 'robinson',
    name: 'Robinson',
    epsg: 'N/A',
    type: 'Compromise pseudo-cylindrical',
    datum: 'WGS84 reference',
    ellipsoid: 'Authalic sphere approximation',
    usage: 'Thematic world maps and atlases',
    distortion: 'Balances area/shape distortion without preserving either exactly.',
    advantages: 'Visually pleasing global compromise projection.',
    disadvantages: 'Not conformal, equal-area, or equidistant.',
    educational: 'Compromise projections are designed for cartographic readability.',
    projector: createD3Projector(geoRobinson),
    bestFor: 'World thematic cartography',
  },
  lambertConformal: {
    id: 'lambertConformal',
    name: 'Lambert Conformal Conic',
    epsg: 'Custom',
    type: 'Conformal conic',
    datum: 'WGS84 reference',
    ellipsoid: 'Spherical form in d3 implementation',
    usage: 'Mid-latitude countries and aviation charts',
    distortion: 'Shape preserved locally, scale varies by standard parallels.',
    advantages: 'Excellent for east-west mid-latitude regions.',
    disadvantages: 'Area and distance are not globally preserved.',
    educational: 'Conic projections fit mid-latitude regions with reduced angular distortion.',
    projector: createD3Projector(() => geoConicConformal().parallels([55, 65]).rotate([-15, 0])),
    bestFor: 'Nordic and European mid-latitudes',
  },
  polarStereo: {
    id: 'polarStereo',
    name: 'Polar Stereographic',
    epsg: 'Custom',
    type: 'Azimuthal conformal',
    datum: 'WGS84 reference',
    ellipsoid: 'Spherical form in d3 implementation',
    usage: 'Polar research and ice mapping',
    distortion: 'Very low near pole; radial scale expands away from pole.',
    advantages: 'Strong fidelity around polar regions.',
    disadvantages: 'Unsuitable for equatorial extents.',
    educational: 'Polar stereographic is ideal when the pole is the map focus.',
    projector: createD3Projector(() => geoStereographic().rotate([0, -90, 0])),
    bestFor: 'Arctic and Antarctic domains',
  },
};

export const projectionOptions = Object.values(projectionCatalog);

export const getProjectionById = (projectionId) =>
  projectionCatalog[projectionId] || projectionCatalog.epsg4326;

export const transformCoordinateSet = (lat, lng) => {
  const mercator = proj4('EPSG:4326', 'EPSG:3857', [lng, lat]);
  const sweref = proj4('EPSG:4326', 'EPSG:3006', [lng, lat]);
  const utm33 = proj4('EPSG:4326', 'EPSG:32633', [lng, lat]);

  return {
    wgs84: { lat, lng },
    mercator: { x: mercator[0], y: mercator[1] },
    sweref99tm: { easting: sweref[0], northing: sweref[1] },
    utm33n: { easting: utm33[0], northing: utm33[1] },
  };
};

export const projectionForDistortion = (projectionId) => {
  if (projectionId === 'epsg4326') {
    return createD3Projector(geoMercator);
  }
  return getProjectionById(projectionId).projector;
};

export const identityProjector = degreeProjection;
